import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import { Bell, Check, CheckCheck } from "lucide-react";

export default function Notifications() {
  const { t, i18n } = useTranslation("notifications");
  const tPath = useTenantPath();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/notifications").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); refresh(); };
  const markAll = async () => { await api.post(`/notifications/read-all`); refresh(); };

  const locale = i18n.language === "en" ? "en-GB" : "pt-PT";

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle", { count: items.filter((n) => !n.read).length })}</p>
        </div>
        <button onClick={markAll} data-testid="mark-all-read" className="inline-flex items-center gap-1.5 text-sm text-zinc-700 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50">
          <CheckCheck className="w-4 h-4" /> {t("markAll")}
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
        {loading ? <div className="p-10 text-center text-sm text-zinc-500">…</div> : items.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="w-8 h-8 text-zinc-300 mx-auto" />
            <div className="text-sm text-zinc-500 mt-2">{t("empty")}</div>
          </div>
        ) : items.map((n) => (
          <div key={n.id} data-testid={`notification-${n.id}`} className={`flex items-center justify-between gap-3 px-5 py-4 ${n.read ? "" : "bg-amber-50/30"}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-amber-500" />}
                <span className="text-sm font-semibold text-zinc-900">{n.title}</span>
              </div>
              <div className="text-sm text-zinc-600 mt-0.5">{n.message}</div>
              <div className="text-xs text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString(locale)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {n.promoter_id && (
                <Link to={tPath(`/promotores/${n.promoter_id}`)} data-testid={`notif-promoter-${n.id}`} className="text-xs font-semibold text-zinc-900 hover:underline">Ver promotor</Link>
              )}
              {n.event_id && (
                <Link to={tPath(`/eventos/${n.event_id}`)} className="text-xs font-semibold text-zinc-900 hover:underline">{t("viewEvent")}</Link>
              )}
              {!n.read && (
                <button onClick={() => markRead(n.id)} data-testid={`mark-read-${n.id}`} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"><Check className="w-3 h-3" /> {t("markRead")}</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
